import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  criteriaFromBriefing,
  getCriteriaTemplates,
  saveCriteriaTemplate,
  WizardApiError,
} from '../campaign-wizard.api';
import { judgingSkipped } from '../campaign-wizard.logic';
import { CriteriaSet, CriteriaTemplate, JudgementClass } from '../campaign-wizard.types';
import { wz } from '../components/wizard-tokens';
import {
  MiSpinner,
  WizCard,
  WizCheckIcon,
  WizChoiceCard,
  WizHeading,
  WizLink,
  WizOutlineButton,
  WizSection,
} from '../components/wizard-ui';
import CriteriaColumn from './judging/criteria-column';
import {
  addCriterion,
  cloneCriteria,
  CriteriaColumn as ColumnKey,
  defaultSaveName,
  defaultTemplate,
  findTemplate,
  isEmptyCriteria,
  isModified,
  removeCriterion,
} from './judging/judging-logic';
import SaveTemplateDialog from './judging/save-template-dialog';
import TemplateDropdown from './judging/template-dropdown';
import type { WizardStepProps } from './step.types';

const COLUMNS: Array<{
  key: ColumnKey;
  titleKey: string;
  hintKey: string;
  variant: 'blue' | 'grey' | 'red';
}> = [
  { key: 'must', titleKey: 'wizJudgMust', hintKey: 'wizJudgMustHint', variant: 'blue' },
  { key: 'nice', titleKey: 'wizJudgNice', hintKey: 'wizJudgNiceHint', variant: 'grey' },
  { key: 'excl', titleKey: 'wizJudgExcl', hintKey: 'wizJudgExclHint', variant: 'red' },
];

/** C = Ausschlusskriterium trifft zu: wird nie angesprochen, die Karte ist nicht wählbar. */
const CLASSES: Array<{ key: JudgementClass; labelKey: string; descKey: string; locked?: boolean }> =
  [
    { key: 'A', labelKey: 'wizJudgClassA', descKey: 'wizJudgClassADesc' },
    { key: 'B', labelKey: 'wizJudgClassB', descKey: 'wizJudgClassBDesc' },
    { key: 'C', labelKey: 'wizJudgClassC', descKey: 'wizJudgClassCDesc', locked: true },
  ];

/** Schritt 3 „KI-Bewertung“: Vorlage, Muss/Wunsch/Ausschluss, anzusprechende Klassen. */
const StepJudging: React.FC<WizardStepProps> = ({ wizard }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { state, patch, showBlocker, blocker } = wizard;

  const [templates, setTemplates] = useState<CriteriaTemplate[] | null>(null);
  const [templatesError, setTemplatesError] = useState(false);
  const [briefingBase, setBriefingBase] = useState<CriteriaSet | null>(null);
  const [fromBriefingBusy, setFromBriefingBusy] = useState(false);
  const [fromBriefingError, setFromBriefingError] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCriteriaTemplates()
      .then(res => {
        if (cancelled) return;
        setTemplates(res.templates);
        const first = defaultTemplate(res.templates, state);
        if (first)
          patch({
            templateId: first.id,
            templateName: first.name,
            judging: cloneCriteria(first.criteria),
            templateSaved: false,
          });
      })
      .catch(() => {
        if (!cancelled) {
          setTemplatesError(true);
          setTemplates([]);
        }
      });
    return () => {
      cancelled = true;
    };
    // Die Vorlagen werden einmal beim Öffnen geladen.
  }, []);

  const selected = useMemo(
    () => (templates ? findTemplate(templates, state) : undefined),
    [templates, state]
  );
  const base = selected ? selected.criteria : state.templateId === 'briefing' ? briefingBase : null;
  const modified = isModified(state.judging, base);
  const templateName = selected?.name ?? state.templateName;

  const setCriteria = (judging: CriteriaSet) => patch({ judging, templateSaved: false });

  const pick = (tpl: CriteriaTemplate) => {
    setFromBriefingError(null);
    patch({
      templateId: tpl.id,
      templateName: tpl.name,
      judging: cloneCriteria(tpl.criteria),
      templateSaved: false,
    });
  };

  const fromBriefing = async () => {
    setFromBriefingBusy(true);
    setFromBriefingError(null);
    try {
      const res = await criteriaFromBriefing(state.briefing);
      setBriefingBase(cloneCriteria(res.criteria));
      patch({
        templateId: 'briefing',
        templateName: t('wizJudgFromBriefingName'),
        judging: cloneCriteria(res.criteria),
        templateSaved: false,
      });
    } catch (e) {
      setFromBriefingError(`${t('wizJudgFromBriefingError')} ${(e as WizardApiError).message}`);
    } finally {
      setFromBriefingBusy(false);
    }
  };

  const openSave = () => {
    setSaveError(null);
    setSaveOpen(true);
  };

  const save = async (name: string, scope: 'team' | 'own') => {
    setSaveBusy(true);
    setSaveError(null);
    try {
      const done = await saveCriteriaTemplate(name, state.judging, scope);
      try {
        setTemplates((await getCriteriaTemplates()).templates);
      } catch {
        setTemplates(prev => [...(prev ?? []).filter(x => x.id !== done.id), done]);
      }
      patch({
        templateId: done.id,
        templateName: done.name,
        judging: cloneCriteria(done.criteria),
        templateSaved: true,
      });
      setSaveOpen(false);
    } catch (e) {
      setSaveError(`${t('wizJudgSaveError')} ${(e as WizardApiError).message}`);
    } finally {
      setSaveBusy(false);
    }
  };

  const classesInvalid = showBlocker && blocker === 'classes';
  // Nur bei eigenen Suchlinks: dort hat der Nutzer die Kandidaten schon selbst ausgewählt (z. B. die Kandidatenliste
  // eines Recruiter-Projekts) — die KI-Bewertung darf entfallen (Simon 22.10.2026).
  const ownSearch = state.method === 'url';
  const skipped = judgingSkipped(state);

  return (
    <WizCard>
      <WizHeading
        title={t('wizJudgTitle')}
        subtitle={t(skipped ? 'wizJudgSkipSubtitle' : 'wizJudgSubtitle')}
      />
      {ownSearch ? (
        <WizChoiceCard
          selected={skipped}
          padding={14}
          gap={10}
          onClick={() => patch({ skipJudging: !skipped })}
        >
          <div className={classes.classBody}>
            <WizCheckIcon checked={skipped} />
            <div className={classes.classText}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t('wizJudgSkipTitle')}</div>
              <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>
                {t('wizJudgSkipDesc')}
              </div>
            </div>
          </div>
        </WizChoiceCard>
      ) : null}
      {skipped ? (
        <div className={classes.muted}>{t('wizJudgSkipKept')}</div>
      ) : (
        <>
          <div className={classes.templateRow}>
            <div className={classes.templateLabel}>{t('wizJudgTemplate')}</div>
            <TemplateDropdown
              name={templateName}
              modified={modified}
              selectedId={selected?.id ?? state.templateId}
              templates={templates}
              error={templatesError}
              busy={fromBriefingBusy}
              onPick={pick}
            />
            <WizOutlineButton
              onClick={() => void fromBriefing()}
              disabled={fromBriefingBusy}
              icon={
                fromBriefingBusy ? <MiSpinner size={16} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />
              }
            >
              {t('wizJudgFromBriefing')}
            </WizOutlineButton>
            <div style={{ flex: 1 }} />
            {state.templateSaved ? (
              <div className={classes.saved}>
                <CheckIcon sx={{ fontSize: 18 }} />
                {t(selected?.scope === 'own' ? 'wizJudgSavedOwn' : 'wizJudgSaved')}
              </div>
            ) : (
              <WizLink onClick={openSave} disabled={isEmptyCriteria(state.judging)}>
                {t('wizJudgSave')}
              </WizLink>
            )}
          </div>
          {fromBriefingError ? <div className={classes.error}>{fromBriefingError}</div> : null}
          <SaveTemplateDialog
            open={saveOpen}
            initialName={defaultSaveName({
              templateName,
              modified,
              suffix: t('wizJudgModified'),
              jobTitle: state.criteria.jobTitles[0],
              campaignName: state.name,
            })}
            templates={templates ?? []}
            busy={saveBusy}
            error={saveError}
            onCancel={() => setSaveOpen(false)}
            onSave={(name, scope) => void save(name, scope)}
          />
          {fromBriefingBusy ? (
            <div className={classes.muted}>{t('wizJudgFromBriefingBusy')}</div>
          ) : null}

          <div className={classes.columns}>
            {COLUMNS.map(c => (
              <CriteriaColumn
                key={c.key}
                title={t(c.titleKey)}
                hint={t(c.hintKey)}
                items={state.judging[c.key]}
                variant={c.variant}
                onAdd={text => setCriteria(addCriterion(state.judging, c.key, text))}
                onRemove={i => setCriteria(removeCriterion(state.judging, c.key, i))}
              />
            ))}
          </div>

          <WizSection gap={12}>
            <div className={classes.reachTitle}>{t('wizJudgClassesTitle')}</div>
            <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>
              {t('wizJudgClassesSubtitle')}
            </div>
            <div className={classes.classes}>
              {CLASSES.map(c => {
                const on = c.locked ? false : state.classes[c.key];
                return (
                  <WizChoiceCard
                    key={c.key}
                    selected={on}
                    padding={14}
                    gap={10}
                    disabled={c.locked}
                    onClick={() => {
                      if (!c.locked)
                        patch({ classes: { ...state.classes, [c.key]: !on, C: false } });
                    }}
                  >
                    <div className={classes.classBody}>
                      <WizCheckIcon checked={on} />
                      <div className={classes.classText}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{t(c.labelKey)}</div>
                        <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>
                          {t(c.descKey)}
                        </div>
                        {c.locked ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: wz.warnFg,
                              lineHeight: 1.4,
                              marginTop: 4,
                            }}
                          >
                            {t('wizJudgClassCLocked')}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </WizChoiceCard>
                );
              })}
            </div>
            {classesInvalid ? <div className={classes.error}>{t('wizMissingClasses')}</div> : null}
          </WizSection>
        </>
      )}
    </WizCard>
  );
};

const useStyles = makeStyles()({
  templateRow: { display: 'flex', alignItems: 'center', gap: 16 },
  templateLabel: { fontSize: 15, fontWeight: 700, width: 70 },
  saved: { fontSize: 14, color: wz.success, display: 'flex', alignItems: 'center', gap: 4 },
  columns: { display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12 },
  reachTitle: { fontSize: 15, fontWeight: 700 },
  classes: { display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12 },
  classBody: { display: 'flex', gap: 10, width: '100%' },
  classText: { display: 'flex', flexDirection: 'column', gap: 4 },
  error: { fontSize: 13, color: wz.redFg },
  muted: { fontSize: 13, color: wz.muted },
});

export default StepJudging;
